<template>
  <!-- 投注紀錄（切版 my-bet.html）：原為取引履歴的 userBet 分頁，獨立成會員頁項目 -->
  <div class="content">
    <div class="my-bet">
      <div ref="tab" class="NavTabs">
        <div
          v-for="day in dayOptions"
          :key="day.value"
          class="tab"
          :class="{ 'active': beforeDay === day.value }"
          @click="switchDay(day.value)"
        >
          {{ $t(day.label) }}
        </div>
      </div>
      <bet-history
        :loading="loading"
        :first-load="firstLoad"
        :record="record"
      />
    </div>
    <scroll-loader :loader-method="query" :loader-disable="!enableAutoLoad" />
  </div>
</template>

<script>
import moment from 'moment'
import { getWalletImagePath, scrollToActive } from '@/utils/tool'
import { gameBetRecord } from '@/api/game'
import BetHistory from '@/components/transaction-history/betHistory'
import ScrollLoader from 'vue-scroll-loader/src/components/Scroll-Loader'

export default {
  components: {
    BetHistory,
    ScrollLoader
  },
  data() {
    return {
      dayOptions: [
        { value: 0, label: 'Bao.today' },
        { value: 1, label: 'Bao.yesterday' },
        { value: 2, label: 'Bao.2_days_ago' },
        { value: 3, label: 'Bao.3_days_ago' },
        { value: 4, label: 'Bao.4_days_ago' },
        { value: 5, label: 'Bao.5_days_ago' },
        { value: 6, label: 'Bao.6_days_ago' },
        { value: 7, label: 'Bao.7_days_ago' }
      ],
      beforeDay: 0,
      record: [],
      page: 1,
      pageSize: 50,
      enableAutoLoad: false,
      loading: false,
      firstLoad: true
    }
  },
  created() {
    this.oneQuery()
  },
  methods: {
    getWalletImagePath,
    async query() {
      if (this.loading) return
      this.loading = true
      this.firstLoad = false

      const queryObj = {
        startDate: Math.floor(moment().subtract(this.beforeDay, 'days').startOf('day').valueOf() / 1000),
        endDate: Math.floor(moment().subtract(this.beforeDay, 'days').endOf('day').valueOf() / 1000),
        page: this.page,
        page_size: this.pageSize
      }

      try {
        const res = await gameBetRecord(queryObj)
        this.checkRecord(res.data.data.list || [])
      } catch (error) {
        this.loading = false
        this.firstLoad = false
        this.enableAutoLoad = false
      }
    },
    checkRecord(list) {
      const newRecordsMap = new Map()

      list.forEach(record => {
        const roundId = record.round_id && record.round_id.trim() ? record.round_id : null
        const walletType = record.wallet_type || ''
        // 組合唯一key
        const key = roundId ? `${roundId}_${walletType}` : null

        if (key) {
          if (!newRecordsMap.has(key)) {
            // 第一次出現
            newRecordsMap.set(key, {
              ...record,
              bet_amount: record.action_type === 'bet' ? record.amount : 0,
              win_amount: record.action_type === 'win' ? record.amount : 0,
              formattedTime: record.updated_at ? this.processedAndSplitDate(record.updated_at) : '-',
              image: this.getWalletImagePath(record)
            })
          } else {
            // 已經有這組key，合併
            const existingRecord = newRecordsMap.get(key)
            if (record.action_type === 'bet') {
              existingRecord.bet_amount = record.amount
            } else if (record.action_type === 'win') {
              existingRecord.win_amount = record.amount
            }
          }
        } else {
          // 沒有round_id，使用Symbol防止key衝突
          newRecordsMap.set(Symbol(), {
            ...record,
            bet_amount: record.action_type === 'bet' ? record.amount : 0,
            win_amount: record.action_type === 'win' ? record.amount : 0,
            image: this.getWalletImagePath(record)
          })
        }
      })

      const newRecords = Array.from(newRecordsMap.values())

      // 舊資料已存在的key集合
      const existingKeys = new Set(
        this.record
          .filter(r => r.round_id && r.round_id.trim())
          .map(r => `${r.round_id}_${r.wallet_type || ''}`)
      )

      // 過濾掉重複
      const filteredNewRecords = newRecords.filter(record => {
        const k = record.round_id ? `${record.round_id}_${record.wallet_type || ''}` : null
        if (k && existingKeys.has(k)) {
          return false
        }
        if (k) existingKeys.add(k)
        return true
      })

      // 合併到 this.record
      this.record = [...this.record, ...filteredNewRecords]

      // 目前的資料上限 (每次 scroll-loader 觸發時，允許的最大數量)
      const maxRecordsAllowed = 50 * this.page

      if (list.length < this.pageSize) {
        // **條件 1:** API 回傳數據 < pageSize，表示資料已撈完，停止自動載入
        this.enableAutoLoad = false
      } else if (this.record.length >= maxRecordsAllowed) {
        // **條件 2:** 如果目前記錄已達當前允許的最大數量，停止載入
        this.enableAutoLoad = false
      } else {
        // **條件 3:** 否則繼續下一頁
        this.page += 1
        this.enableAutoLoad = true
      }

      this.loading = false
      this.firstLoad = false

      this.$nextTick(() => {
        this.checkAndLoadMore()
      })
    },
    oneQuery() {
      this.page = 1
      this.enableAutoLoad = true
      this.record = []
      this.query()
    },
    processedAndSplitDate(formattedTime) {
      return this.$options.filters.fullDateTimeFormat(formattedTime)
    },
    switchDay(beforeDay) {
      if (this.beforeDay === beforeDay) return
      this.beforeDay = beforeDay
      this.oneQuery()

      this.$nextTick(() => {
        scrollToActive(this.$refs.tab)
      })
    },
    checkAndLoadMore() {
      // 1. 檢查：如果還可以載入更多資料
      if (!this.enableAutoLoad || this.loading) {
        return // 如果不能載入或正在載入，就不執行
      }

      // 2. 檢查：scroll-loader 的 loading UI 是否在可視範圍內
      const scrollLoaderElement = document.querySelector('.loader')
      if (scrollLoaderElement) {
        const rect = scrollLoaderElement.getBoundingClientRect()
        const isVisible = rect.top < window.innerHeight && rect.bottom >= 0

        // 3. 如果兩個條件都滿足，自動觸發載入
        if (isVisible) {
          this.query() // 自動觸發下一次載入
        }
      }
    }
  }
}
</script>
